import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import Helper from "../../../../Common/Helper";
import { PagedResponse } from "@iconics/core";
import { Attribute, Entity, Package, Solution } from "./DataModels";
import { JSX } from "react";
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { MultilingualTextField } from "@iconics/components";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { Switch } from "@serendie/ui";
import { Validator } from '@iconics/core';
import { BuiltInType } from "../../../../Providers.FadxPlatformApiClientV2/FadxPlatformApiClientV2";
import { Button, ButtonBar, Card, DataGrid, DeleteButton, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, TextField, TickSymbol } from "@iconics/components";

class EditPackage extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private labels = appContext().labels;
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state = {
            canDelete: false,
            canSetupEntity: false,
            canSetupAttribute: false,
            tab: 0,
            isEnabled: false,
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            description: '',
            descriptionError: '',
            eTag: '',
            solutionsGridData: { rows: [] },
            entitiesGridData: { rows: [] },
            attributesGridData: { rows: [] },
            sections: [],
        };
    }

    private get isNew(): boolean
    {
        return (!this.props.params.id);
    }

    public async componentDidMount(): Promise<void>
    {
        const labels = appContext().labels;
        this.pageTitle = labels.Platform_Package_PageTitle;
        this.backUrl = '/admin/packages';

        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();

        this.validator
            .addInput('description', () => this.state.description, i => this.setState({ descriptionError: i }))
            .isRequired();

        this.setState({
            canDelete: appContext().localStorageProvider.hasRight('DATAMODEL.Packages.Delete'),
            canSetupEntity: appContext().localStorageProvider.hasRight('ADMINPORTAL.Entities.Setup'),
            canSetupAttribute: appContext().localStorageProvider.hasRight('ADMINPORTAL.Attributes.Setup'),
        });

        if (!this.isNew)
        {
            await this.loading(async () =>
            {
                try
                {
                    await this.loadDetails();
                }
                catch (error)
                {
                    console.log(error);
                }
            });
        }
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.packages.byId.delete({
                id: this.props.params.id ?? '',
                tenancy: appContext().tenantId,
            });
            this.props.navigate(`/admin/packages`);
        });
    }

    private handleCancel(): void
    {
        this.props.navigate(`/admin/packages`);
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () => 
        {
            if (this.isNew)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.packages.post({
                    body: {
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        Name: this.state.name,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        Description: this.state.description,
                    },
                    tenancy: appContext().tenantId,
                });
            }
            else
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.packages.byId.patch({
                    body: {
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        Name: this.state.name,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        Description: this.state.description,
                        "@odata.etag": this.state.eTag,
                    },
                    id: this.props.params.id ?? '',
                    tenancy: appContext().tenantId,
                });
            }
            this.props.navigate(`/admin/packages`);
        });
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer
                        spacing={5}
                    >
                        <GridItem>
                            {this.renderDetails()}
                        </GridItem>
                        {
                            !this.isNew &&
                            <>
                                <GridItem>
                                    {this.renderSolutions()}
                                </GridItem>
                                <GridItem>
                                    {this.renderEntities()}
                                </GridItem>
                                <GridItem>
                                    {this.renderAttributes()}
                                </GridItem>
                            </>
                        }
                    </GridContainer>
                </LayoutContent>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                !this.isNew &&
                                <DeleteButton
                                    disabled={!this.state.canDelete}
                                    dialogMessage={this.labels.Platform_Package_ConfirmDeleteMsg}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.handleCancel()}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                disabled={!this.validator.isValid}
                                onClick={() => this.handleSave()}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
            </LayoutContainer>
        );
    }

    private async loadDetails(): Promise<void>
    {
        const _package = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.packages.byId.get<Package>({
            id: this.props.params.id ?? '',
            select: Package,
            tenancy: appContext().tenantId,
        });

        this.setState({
            isEnabled: _package.IsEnabled,
            name: _package.Name,
            displayName: this.languageValuesHelper.fromString(_package.DisplayName),
            description: _package.Description,
            eTag: _package["@odata.etag"],
        });
    }

    private renderDetails(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_Package_DetailsTitle}
                subTitle={this.labels.Platform_Package_DetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                icon={<SerendieSymbol name='file' />}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Package_Name}
                            placeholder={this.labels.Package_Name}
                            value={this.state.name}
                            onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                            required
                            invalid={!!this.state.nameError}
                            invalidMessage={this.state.nameError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <MultilingualTextField
                            label={this.labels.Package_DisplayName}
                            placeholder={this.labels.Package_DisplayName}
                            value={this.state.displayName}
                            onChange={value => this.setState({ displayName: value })}
                            localisation={appContext().localisation}
                        />
                    </GridItem>
                    <GridItem>
                        <TextField
                            label={this.labels.Package_Description}
                            placeholder={this.labels.Package_Description}
                            value={this.state.description}
                            onChange={e => this.setState({ description: e.target.value }, () => this.validator.validate('description'))}
                            required
                            invalid={!!this.state.descriptionError}
                            invalidMessage={this.state.descriptionError}
                        />
                    </GridItem>
                    <GridItem>
                        <Switch
                            label={this.labels.Package_IsEnabled}
                            checked={this.state.isEnabled}
                            onCheckedChange={e => this.setState({ isEnabled: e.checked as boolean })}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    private async handleSolutionsRowsRequest(request: IGridRowsRequest<ISolution>): Promise<IGridRowsResponse<ISolution>>
    {
        const solutions = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.packages.byId.solutions.get<PagedResponse<Solution[]>>({
            tenancy: appContext().tenantId,
            id: this.props.params.id ?? '',
        });

        return {
            rows: solutions.value.map(i => ({
                id: i.Id,
                eTag: i["@odata.etag"],
                isEnabled: i.IsEnabled,
                name: i.Name,
                description: i.Description,
            })), nextSkipToken: solutions.skipToken
        };
    }

    private handleEditSolution(solutionId: string): void
    {
        this.props.navigate(`/admin/solutions/${solutionId}/edit`);
    }

    private renderSolutions(): JSX.Element
    {
        const labels = appContext().labels;

        return (
            <Card
                title={this.labels.Platform_Package_SolutionListTitle}
                subTitle={this.labels.Platform_Package_SolutionListSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
            >
                <GridContainer>
                    <GridItem>
                        <DataGrid
                            columns={[
                                {
                                    columnId: Helper.nameOf<Solution>("Name"),
                                    title: labels.Solution_Name,
                                    getCellValue: i => i.name,
                                    sortable: true,
                                    columnWidth: 40,
                                },
                                {
                                    columnId: Helper.nameOf<Solution>("Description"),
                                    title: labels.Solution_Description,
                                    getCellValue: i => i.description,
                                    sortable: true,
                                    columnWidth: 40,
                                },
                                {
                                    columnId: Helper.nameOf<Solution>("IsEnabled"),
                                    title: labels.Solution_IsEnabled,
                                    getCellValue: (i) => <TickSymbol value={i.isEnabled} />,
                                    sortable: true,
                                    columnWidth: 20,
                                },
                            ]}
                            data={this.state.solutionsGridData}
                            rowId={i => i.id.toString()}
                            onChange={i => this.setState({ solutionsGridData: i })}
                            onRowsRequest={i => this.handleSolutionsRowsRequest(i)}
                            onCellClicked={i => this.handleEditSolution(i.id)}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    private async handleEntitiesRowsRequest(request: IGridRowsRequest<IEntity>): Promise<IGridRowsResponse<IEntity>>
    {
        const entities = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.entities.get<PagedResponse<Entity[]>>({
            tenancy: appContext().tenantId,
            filter: `Package_ID eq '${this.props.params.id ?? ''}'`,
            select: Entity,
            top: request.take,
            skipToken: request.skipToken,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        return {
            rows: entities.value.map(i => ({
                id: i.Id,
                name: i.Name,
                displayName: i.DisplayName ?? '',
                storageType: i.StorageType,
                historyStorageMethod: i.HistoryStorageMethod,
                auditLogDisabled: i.AuditLogDisabled,
            })), nextSkipToken: entities.skipToken
        };
    }

    private handleEditEntity(entityId?: number): void
    {
        if (!this.state.canSetupEntity || !this.props.params.id)
        {
            return;
        }
        else if (!entityId)
        {
            this.props.navigate(`/admin/packages/${this.props.params.id}/entities/new`);
        }
        else
        {
            this.props.navigate(`/admin/packages/${this.props.params.id}/entities/${entityId}/edit`);
        }
    }

    private renderEntities(): JSX.Element
    {
        const labels = appContext().labels;

        return (
            <Card
                title={this.labels.Platform_Package_EntityListTitle}
                subTitle={this.labels.Platform_Package_EntityListSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 2) }))}
            >
                <GridContainer>
                    <GridItem>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='plus' />}
                                    onClick={() => this.handleEditEntity()}
                                    disabled={!this.state.canSetupEntity}
                                >
                                    {this.labels.Platform_CommonAction_Add}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </GridItem>
                    <GridItem>
                        <DataGrid
                            columns={[
                                {
                                    columnId: Helper.nameOf<Entity>("Name"),
                                    title: labels.Entity_Name,
                                    getCellValue: i => i.name,
                                    sortable: true,
                                    columnWidth: 20,
                                },
                                {
                                    columnId: Helper.nameOf<Entity>("DisplayName"),
                                    title: labels.Entity_DisplayName,
                                    getCellValue: i => i.displayName,
                                    sortable: true,
                                    columnWidth: 20,
                                },
                                {
                                    columnId: Helper.nameOf<Entity>("StorageType"),
                                    title: labels.Entity_StorageType,
                                    getCellValue: i => i.storageType,
                                    sortable: true,
                                    columnWidth: 20,
                                },
                                {
                                    columnId: Helper.nameOf<Entity>("HistoryStorageMethod"),
                                    title: labels.Entity_HistoryStorageMethod,
                                    getCellValue: i => i.historyStorageMethod,
                                    sortable: true,
                                    columnWidth: 20,
                                },
                                {
                                    columnId: Helper.nameOf<Entity>("AuditLogDisabled"),
                                    title: labels.Entity_AuditLogDisabled,
                                    getCellValue: i => <TickSymbol value={i.auditLogDisabled} />,
                                    sortable: true,
                                    columnWidth: 20,
                                },
                            ]}
                            data={this.state.entitiesGridData}
                            rowId={i => i.id.toString()}
                            onChange={i => this.setState({ entitiesGridData: i })}
                            onRowsRequest={i => this.handleEntitiesRowsRequest(i)}
                            onCellClicked={i => this.handleEditEntity(i.id)}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    private async handleAttributeRowsRequest(request: IGridRowsRequest<IAttribute>): Promise<IGridRowsResponse<IAttribute>>
    {
        const attributesData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.attributes.get<PagedResponse<Attribute[]>>({
            select: Attribute,
            tenancy: appContext().tenantId,
            filter: `Package_ID eq '${this.props.params.id ?? ''}'`,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
            top: request.take,
            skipToken: request.skipToken,
        });

        const attributes = attributesData.value.map(i => ({
            id: i.Id,
            name: i.Name,
            builtInType: i.BuiltInType,
            isArray: i.IsArray ?? false,
            isRequired: i.IsMandatory ?? false,
        } satisfies IAttribute));

        return {
            rows: attributes,
            nextSkipToken: attributesData.skipToken,
        };
    }

    private handleEditAttribute(attributeId?: number): void
    {
        if (!this.state.canSetupAttribute || !this.props.params.id)
        {
            return;
        }
        else if (!attributeId)
        {
            this.props.navigate(`/admin/packages/${this.props.params.id}/attributes/new`);
        }
        else
        {
            this.props.navigate(`/admin/packages/${this.props.params.id}/attributes/${attributeId}/edit`);
        }
    }

    private renderAttributes(): JSX.Element
    {
        const labels = appContext().labels;

        return (
            <Card
                title={this.labels.Platform_Entity_AttributeListTitle}
                subTitle={this.labels.Platform_Entity_AttributeListSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 3) }))}
            >
                <GridContainer>
                    <GridItem>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='plus' />}
                                    onClick={() => this.handleEditAttribute()}
                                    disabled={!this.state.canSetupAttribute}
                                >
                                    {this.labels.Platform_CommonAction_Add}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </GridItem>
                    <GridItem>
                        <DataGrid
                            columns={[
                                {
                                    columnId: Helper.nameOf<Attribute>("Name"),
                                    title: labels.Attribute_Name,
                                    getCellValue: i => i.name,
                                    sortable: true,
                                    columnWidth: 30,
                                },
                                {
                                    columnId: Helper.nameOf<Attribute>("BuiltInType"),
                                    title: labels.Attribute_BuiltInType,
                                    getCellValue: i => i.builtInType,
                                    sortable: true,
                                    columnWidth: 30,
                                },
                                {
                                    columnId: Helper.nameOf<Attribute>("IsArray"),
                                    title: labels.Attribute_IsArray,
                                    getCellValue: i => <TickSymbol value={i.isArray} />,
                                    sortable: true,
                                    columnWidth: 20,
                                },
                                {
                                    columnId: Helper.nameOf<Attribute>("IsMandatory"),
                                    title: labels.Attribute_IsRequired,
                                    getCellValue: i => <TickSymbol value={i.isRequired} />,
                                    sortable: true,
                                    columnWidth: 20,
                                },
                            ]}
                            data={this.state.attributesGridData}
                            onChange={i => this.setState({ attributesGridData: i })}
                            rowId={i => i.id.toString()}
                            onRowsRequest={i => this.handleAttributeRowsRequest(i)}
                            onCellClicked={i => this.handleEditAttribute(i.id)}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }
}

export default withRouting(EditPackage);

export interface IProps extends IRouterProps<IRouteParams>
{
}

export interface IRouteParams
{
    id: string | undefined;
}

export interface IState
{
    canDelete: boolean;
    canSetupEntity: boolean;
    canSetupAttribute: boolean;
    tab: number;
    isEnabled: boolean;
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    description: string;
    descriptionError: string;
    eTag: string;
    solutionsGridData: IGridData<ISolution>;
    entitiesGridData: IGridData<IEntity>;
    attributesGridData: IGridData<IAttribute>;
    sections: IPageNavSection[];
}

export interface ISolution
{
    id: string;
    eTag: string;
    isEnabled: boolean;
    name: string;
    description: string;
}

export interface IEntity
{
    id: number;
    name: string;
    displayName: string;
    historyStorageMethod: string;
    storageType: string;
    auditLogDisabled: boolean;
}

export interface IAttribute
{
    id: number;
    name: string;
    builtInType: BuiltInType | '';
    isArray: boolean;
    isRequired: boolean;
}
